import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HardHat, Mail, Lock, User, UserCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { setAuth } from "../utils/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = ({ setUser }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "Engineer"
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        setAuth(response.data.token, response.data.user);
        setUser(response.data.user);
        toast.success("Login successful");
        
        // Redirect HR Manager directly to Projects, others to Dashboard
        if (response.data.user.role === "HR Manager") {
          navigate("/projects");
        } else {
          navigate("/");
        }
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        toast.success("Registration submitted! Waiting for admin approval.");
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left branding panel - hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-slate-900 to-slate-700">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="relative z-10 flex items-center justify-center w-full px-12">
          <div className="text-center text-white">
            <div className="mb-8">
              <img 
                src="/kpm-logo.png" 
                alt="KPM Infrastructure" 
                className="w-32 h-32 mx-auto mb-6 object-contain bg-white rounded-lg p-2"
              />
            </div>
            <h1 className="font-heading font-black text-6xl mb-4 tracking-tight">KPM INFRASTRUCTURE</h1>
            <div className="h-1 w-32 bg-accent mx-auto mb-6"></div>
            <p className="text-2xl font-body mb-2">Private Limited</p>
            <p className="text-xl font-body text-slate-300 mt-8">Project Monitoring & Management System</p>
            <p className="text-lg font-body text-slate-400 mt-4">Daily Progress Tracking | BOQ Management | Analytics</p>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-white rounded-lg shadow-md mb-4">
              <img 
                src="/kpm-logo.png" 
                alt="KPM Infrastructure" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h2 className="font-heading font-bold text-3xl text-primary mb-2">
              {isLogin ? "WELCOME BACK" : "CREATE ACCOUNT"}
            </h2>
            <p className="text-slate-600 text-sm font-body">KPM Infrastructure Pvt. Ltd.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="register-name-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-body"
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                    Role
                  </label>
                  <div className="relative">
                    <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <select
                      data-testid="register-role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-body"
                    >
                      {/* SEC-003 fix: Only show non-privileged roles for self-registration */}
                      <option value="Site Engineer">Site Engineer</option>
                      <option value="Store Keeper">Store Keeper</option>
                      <option value="Accountant">Accountant</option>
                      <option value="Data Entry Operator">Data Entry Operator</option>
                      <option value="HR Manager">HR Manager</option>
                      <option value="Project Coordinator">Project Coordinator</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Privileged roles (Admin, Director, Manager) are assigned by administrators after account approval.
                    </p>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  data-testid="email-input"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-body"
                  placeholder="your.name@kpminfra.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-body"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid={isLogin ? "login-submit-button" : "register-submit-button"}
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "PROCESSING..." : (isLogin ? "LOGIN" : "REGISTER")}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className="text-sm text-slate-600 hover:text-primary transition-colors"
            >
              {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
            </button>
          </div>
          
          <div className="mt-8 text-center text-xs text-slate-500">
            <p>&copy; 2026 KPM Infrastructure Private Limited</p>
            <p className="mt-1">All Rights Reserved</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;